import { useEffect, useState, useContext } from 'react';
import ReactDatePicker from "react-datepicker";
import { useNavigate } from 'react-router-dom';
import Select from "react-select";
import { getBeatsApi, getCustomersApi, getVouchersApi, saveReceiptApi, getUserListApi } from '../api/api';
import { AuthContext } from '../context/AuthContext.jsx';
import { useOrganizationSettings } from '../context/OrganizationSettingsContext';
import { useCompany } from '../context/CompanyContext.jsx';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import './ReceiptEditForm.css';

export default function ReceiptEntryForm() {
  const { user } = useContext(AuthContext);
  const { settings } = useOrganizationSettings();
  const { companyId } = useCompany();
  const navigate = useNavigate();
  const [userList, setUserList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState(null);
  const [beats, setBeats] = useState([]);
  const [selectedBeat, setSelectedBeat] = useState('');
  const [selectedReceiver, setSelectedReceiver] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [vouchers, setVouchers] = useState([]);
  const [totalBillAmount, setTotalBillAmount] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [totalRemainingAmount, setTotalRemainingAmount] = useState(0);
  const [chequeDate, setChequeDate] = useState(null);
  const [upiDate, setUpiDate] = useState(null);
  const [receiptDate, setReceiptDate] = useState(new Date());
  const [upiFileImage, setUpiFile] = useState(null);

  const [form, setForm] = useState({
    tallyId: user.tallyId,
    companyId: companyId,
    companyName: settings.companyName,
    receiptDate: new Date().toISOString().slice(0, 10),
    tallyCustomerId: null,
    cashAmount: null,
    upiAmount: null,
    chequeAmount: null,
    upiNumber: null,
    upiDate: null,
    chequeNumber: null,
    chequeDate: null,
    bankName: null,
    remark: null,
    advanceAmount: null,
    billAllocations: [],
    billErrors: [],
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (msg) {
      const timer = setTimeout(() => {
        setMsg('');
      }, 3000); // 3 seconds
      return () => clearTimeout(timer);
    }
  }, [msg]);

  // disable/clear UPI fields when upiAmount is empty or zero
  useEffect(() => {
    const enabled = parseFloat(form.upiAmount || 0) > 0;
    if (!enabled) {
      // clear UPI related values (form + local state)
      setForm(prev => ({ ...prev, upiNumber: '', upiDate: null }));
      setUpiDate(null);
      setUpiFile(null);
    }
  }, [form.upiAmount]);
  
  // disable/clear Cheque fields when chequeAmount is empty or zero
  useEffect(() => {
    const enabled = parseFloat(form.chequeAmount || 0) > 0;
    if (!enabled) {
      // clear UPI related values (form + local state)
      setForm(prev => ({ ...prev, chequeNumber: '', chequeDate: null }));
      setChequeDate(null);
    }
  }, [form.chequeAmount]);

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Get only base64 part
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  // Fetch all users on mount (only if userWise is enabled and user is owner)
    useEffect(() => {
      if (settings.userWise && user?.isOwner) {
        getUserListApi()
          .then(res => {
            const users = res.data?.data || res.data || [];
            setUserList(users);
            
            // Set default selection to logged-in user for owners
            if (user?.tallyUserId && users.length > 0) {
              const defaultUser = users.find(sp => {
                const spId = sp.tallyUserId || sp.tallyUserId;
                const userId = user.tallyUserId || user.tallyUserId;
                return spId === userId;
              });
              if (defaultUser) {
                setSelectedUserId(defaultUser.tallyUserId);
                setSelectedUserLabel(`${defaultUser.userName} - ${defaultUser.tallyUserId}`);
              }
            }
          })
          .catch(() => setUserList([]));
      }
    }, [settings.userWise, user?.isOwner, user?.tallyUserId]);

  useEffect(() => {
    // if (!selectedUserId) { setBeats([]); return; }
    getBeatsApi({ tallyUserId: selectedUserId })
      .then((res) => setBeats(res.data?.data || []))
      .catch(() => setBeats([]));
  }, [selectedUserId]);

  useEffect(() => {
    // If beatWise is false, fetch all customers without requiring beat selection
    if (!settings.beatWise) {
      getCustomersApi({})
        .then(res => setCustomers(res.data?.data || []))
        .catch(() => setCustomers([]));
      return;
    }
    // If beatWise is true, only fetch when beat is selected
    if (!selectedBeat) { 
      setCustomers([]); 
      return; 
    }
    getCustomersApi({ beatName: selectedBeat })
      .then(res => setCustomers(res.data?.data || []))
      .catch(() => setCustomers([]));
  }, [selectedBeat, settings.beatWise]);

  useEffect(() => {
    // If beatWise is false, only require customer; if beatWise is true, require both beat and customer
    if (!selectedCustomer || (settings.beatWise && !selectedBeat)) {
      setVouchers([]);
      setTotalBillAmount(0);
      setTotalPaidAmount(0);
      setTotalRemainingAmount(0);
      return;
    }
    setLoading(true);
    const params = {
      customerName: selectedCustomer,
      ...(settings.beatWise && selectedBeat ? { beatName: selectedBeat } : {}),
      ...(settings.userWise && companyId ? { companyId: companyId } : {}),
    };
    getVouchersApi(params)
      .then((res) => {
        if(res.data?.totalRemainingAmount > 0){
          setVouchers(res.data?.data || []);
        }
        setTotalBillAmount(res.data?.totalBillAmount || 0);
        setTotalPaidAmount(res.data?.totalPaidAmount || 0);
        setTotalRemainingAmount(res.data?.totalRemainingAmount || 0);
      })
      .catch(() => {
        setVouchers([]);
        setTotalBillAmount(0);
        setTotalPaidAmount(0);
        setTotalRemainingAmount(0);
      })
      .finally(() => setLoading(false));
  }, [selectedBeat, selectedCustomer, settings.beatWise, settings.userWise, companyId]);

  const handleUserChange = e => {
    // Only update if a selection was actually made (not when clicking outside)
    if (e === null) {
      // Only clear if explicitly cleared (X button), not on outside click
      setSelectedUserId(null);
      setSelectedUserLabel(null);
    } else if (e && e.value) {
      setSelectedUserId(e.value);
      setSelectedUserLabel(e.label || '');
      setSelectedBeat('');
      setSelectedCustomer('');
      setBeats([]);
      setCustomers([]);
      setVouchers([]);
      setTotalBillAmount(0);
      setTotalPaidAmount(0);
      setTotalRemainingAmount(0);
    }
  };

  const handleBeatChange = e => {
    setSelectedBeat(e.target.value);
    setSelectedCustomer('');
    setVouchers([]);
    setTotalBillAmount(0);
    setTotalPaidAmount(0);
    setTotalRemainingAmount(0);
    handleRemark(e.target.value);
  };
  
  const handleReceiverChange = e => {
    setSelectedReceiver(e.target.value);
  };
  
  const handleCustomerChange = e => {
    setSelectedCustomer(e.target.value);
    setVouchers([]);
    setTotalBillAmount(0);
    setTotalPaidAmount(0);
    setTotalRemainingAmount(0);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOnlyNumber = (e) => {
    // Allow: Backspace, Delete, Tab, Escape, Enter, period (.),
    // numbers 0-9, arrow keys, minus (-) for negative numbers
    if (
      [8, 9, 27, 13, 46, 110, 190].includes(e.keyCode) || // control keys & decimal point
      (e.key >= '0' && e.key <= '9') ||                   // numbers
      ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) || // navigation
      (e.key === '-' && e.target.selectionStart === 0)    // minus sign only at start
    ) {
      // Allow input
    } else {
      e.preventDefault();
    }
  }

  const handleBillAllocations = (billNumber, value, billData) => {
    let errorMsg = "";
    const balance = billData.balance ?? billData.remainingAmount ?? 0;
    if (value && parseFloat(value) > parseFloat(balance)) {
      errorMsg = "Allocation amount cannot exceed the available balance";
    }
    
    setForm(prev => {
      const existing = prev.billAllocations || [];
      const errors = prev.billErrors || {};
      const idx = existing.findIndex(item => item.billNumber === billNumber);

      let newAllocations;
      if (idx === -1) {
        newAllocations = [...existing, { billNumber, billPaidAmount: value, billData }];
      } else {
        newAllocations = [...existing];
        newAllocations[idx] = { billNumber, billPaidAmount: value, billData };
      }

      // Update errors for this bill
      const newErrors = { ...errors, [billNumber]: errorMsg };
      
      return {
        ...prev,
        billAllocations: newAllocations,
        billErrors: newErrors
      };
    });
  };

  const getAllocationValue = (billNumber) => {
    const item = form.billAllocations.find(i => i.billNumber === billNumber);
    return item ? item.billPaidAmount : '';
  };

  const handleAdvanceAmount = () =>{
    // const totalAllocated = form.billAllocations.reduce(
    //   (sum, item) => sum + (parseFloat(item.billPaidAmount) || 0),
    //   0
    // );
    // Calculate total payment entered
    const cash = parseFloat(form.cashAmount) || 0;
    const upi = parseFloat(form.upiAmount) || 0;
    const cheque = parseFloat(form.chequeAmount) || 0;
    const totalPaid = cash + upi + cheque;
    // console.log("totalPaid", totalPaid)
    // const advanceAmount = totalPaid - totalAllocated;
    let advanceAmount = totalPaid - totalRemainingAmount;
    if(advanceAmount < 0) advanceAmount = null;
    setForm(prev => ({
      ...prev,
      advanceAmount
    }));
  }
  const handleRemark = (beat) =>{
    let remarkText = `${beat}, ${selectedUserLabel || user.userName}, ${form.receiptDate}`;
    setForm(prev => ({
      ...prev,
      remark: remarkText
    }));
  }

// bills: your vouchers/bill array (with balance or remainingAmount field)
// form: your form state (must have cashAmount, upiAmount, chequeAmount as string/numeric, and billAllocations)
function autoAllocateBillsAndAdvance(bills, form, setForm) {
  // Clear existing allocations first by setting billPaidAmount to 0
  const clearedAllocations = (form.billAllocations || []).map(item => ({
    ...item,
    billPaidAmount: ''
  }));

  // Calculate total payment available
  const cash = parseFloat(form.cashAmount) || 0;
  const upi = parseFloat(form.upiAmount) || 0;
  const cheque = parseFloat(form.chequeAmount) || 0;
  let toAllocate = cash + upi + cheque;

  // Assign clearedAllocations for quick lookup by billNumber
  const clearedAllocMap = new Map(clearedAllocations.map(item => [item.billNumber, item]));

  const newAllocations = [];
  for (const bill of bills) {
    const balance = parseFloat(bill.balance ?? bill.remainingAmount ?? null);
    const existingAllocation = clearedAllocMap.get(bill.billNumber);

    if (toAllocate > 0) {
      // Allocate min of balance or remaining toAllocate
      const pay = Math.min(balance, toAllocate);
      newAllocations.push({
        billNumber: bill.billNumber,
        billPaidAmount: pay,
        billData: bill
      });
      toAllocate -= pay;
    } else {
      newAllocations.push({
        billNumber: bill.billNumber,
        billPaidAmount: '',
        billData: bill
      });
    }
  }

  const advanceAmount = toAllocate;

  setForm(prevForm => ({
    ...prevForm,
    billAllocations: newAllocations,
    billErrors: {},
    // advanceAmount
  }));
}


function autoAllocateBillsAndAdvanceOld(bills, form, setForm) {
  const clearedAllocations = (form.billAllocations || []).map(item => ({
    ...item,
    billPaidAmount: null
  }));

  console.log("clearedAllocations", clearedAllocations);
  // Calculate total payment available
  const cash = parseFloat(form.cashAmount) || 0;
  const upi = parseFloat(form.upiAmount) || 0;
  const cheque = parseFloat(form.chequeAmount) || 0;
  let toAllocate = cash + upi + cheque;

  // Assign clearedAllocations for quick lookup by billNumber
  const clearedAllocMap = new Map(clearedAllocations.map(item => [item.billNumber, item]));

  const newAllocations = [];
  for (const bill of bills) {
    const balance = parseFloat(bill.balance ?? bill.remainingAmount ?? 0);
    const existingAllocation = clearedAllocMap.get(bill.billNumber);

    if (toAllocate > 0) {
      const pay = Math.min(balance, toAllocate);
      newAllocations.push({ billNumber: bill.billNumber, billPaidAmount: pay, billData: bill });
      toAllocate -= pay;
    } else {
      newAllocations.push({ billNumber: bill.billNumber, billPaidAmount: null, billData: bill });
    }
  }
  // The leftover is the advance
  const advanceAmount = toAllocate;

  setForm(prevForm => ({
    ...prevForm,
    billAllocations: newAllocations,
    advanceAmount
  }));
}

  const resetAdvanceAmount = () => {
    setForm(prevForm => ({
      ...prevForm,
      advanceAmount: null,
      billAllocations: [],
      billErrors: []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    // Basic validation
    if (
      (!form.cashAmount || parseFloat(form.cashAmount) === 0) &&
      (!form.upiAmount || parseFloat(form.upiAmount) === 0) &&
      (!form.chequeAmount || parseFloat(form.chequeAmount) === 0)
    ) {
      setMsg('Please enter at least one payment amount (Cash, UPI, or Cheque).');
      setSaving(false);
      return;
    }
    if(form.upiAmount && (!form.upiNumber || !upiDate)) {
      setMsg('Please provide UPI Number and UPI Date for UPI payments.');
      setSaving(false);
      return;
    }
    if(form.chequeAmount && (!form.chequeNumber || !chequeDate)) {
      setMsg('Please provide Cheque Number and Cheque Date for Cheque payments.');
      setSaving(false);
      return;
    }
    
    // Bill allocation validations - only when billWise is enabled
    if (settings.billWise) {
      if (form.billErrors.length == 0) {
        setMsg('Please adjust the bill allocation amounts before submitting.');
        setSaving(false);
        return;
      }

      const hasBillErrors = Object.values(form.billErrors).some(err => err);
      if (hasBillErrors) {
        setMsg('Please adjust the bill allocation amounts before submitting.');
        setSaving(false);
        return;
      }

      // Calculate total entered in bill allocations
      const totalAllocated = form.billAllocations.reduce(
        (sum, item) => sum + (parseFloat(item.billPaidAmount) || 0),
        0
      );
      
      // Calculate total payment entered
      const cash = parseFloat(form.cashAmount) || 0;
      const upi = parseFloat(form.upiAmount) || 0;
      const cheque = parseFloat(form.chequeAmount) || 0;
      const advanceAmount = form.advanceAmount ? parseFloat(form.advanceAmount) : 0;
      const totalPaid = cash + upi + cheque;
      const totalAllocatedWithAdvance = totalAllocated + advanceAmount;

      // Validation: amounts must match
      if (totalAllocatedWithAdvance !== totalPaid) {
        setMsg('Total bill allocation amount and advance amount must match the sum of Cash, UPI, and Cheque amounts.');
        setSaving(false);
        return;
      }
      
      const checkAdvanceAmount = Math.sign(advanceAmount);
      if (checkAdvanceAmount === -1) {
        setMsg('Advance amount should not be in negative. Please adjust the bill allocation amounts.');
        setSaving(false);
        return;
      }
    }
    
    try {
      let upiFileBase64 = null;
      if (upiFileImage) {
        upiFileBase64 = await fileToBase64(upiFileImage);
      }
      // Prepare server payload as your API expects
      const payload = {
        ...form,
        tallyUserId: settings.userWise ? (user?.isOwner ? selectedUserId : user?.tallyUserId) : null,
        beatName: settings.beatWise ? selectedBeat : null,
        tallyCustomerId: null,
        customerName: selectedCustomer,
        receiver: selectedReceiver,
        chequeDate: (chequeDate) ? chequeDate.toISOString().slice(0, 10) : null,
        upiDate: (upiDate) ? upiDate.toISOString().slice(0, 10) : null,
        receiptDate: receiptDate.toISOString().slice(0, 10),
        upiFileImage: upiFileBase64, // Send base64 string
        upiFileName: upiFileImage ? upiFileImage.name : null, // Optionally send file name
        billAllocations: settings.billWise ? form.billAllocations : [],
        ...(settings.userWise && companyId ? { companyId: companyId } : {}),
      };
      // console.log("payload -- ", payload);
      // return false;
      await saveReceiptApi(payload);
      setMsg('Receipt saved successfully.');
      // Reset minimal fields
      setForm(f => ({
        ...f,
        customerName: null,
        cashAmount: '',
        upiAmount: '',
        upiNumber: '',
        upiDate: null,
        chequeAmount: '',
        chequeNumber: '',
        chequeDate: null,
        bankName: '',
        remark: '',
        advanceAmount: '', 
        billAllocations: [],
        billErrors: []
      }));
      setSelectedCustomer('');
      setSelectedReceiver('');
      setChequeDate(null);
      setUpiDate(null);
      setUpiFile(null);
      setVouchers([]);
      setTotalBillAmount(0);
      setTotalPaidAmount(0);
      setTotalRemainingAmount(0);
      // Redirect to dashboard
      // navigate('/receipts/list');
      // window.location.reload();
    } catch (err) {
      console.log(err)
      setMsg('Failed to save Receipt.');
    } finally {
      setSaving(false);
    }
  };

  const receiverOptions = [
    { value: 'Supply 1', label: 'Supply 1' },
    { value: 'Supply 2', label: 'Supply 2' },
    { value: 'Supply 3', label: 'Supply 3' },
    { value: 'Supply 4', label: 'Supply 4' },
    { value: 'Supply 5', label: 'Supply 5' },
  ];

  return (
    <div className="receipt-form-container">
      <div className="receipt-form-card">
        <form onSubmit={handleSubmit}>
          <div className="receipt-form-header">
            <h4>Receipt Entry</h4>
          </div>

          {/* Basic Information Section */}
          <div className="receipt-form-section">
            <div className="receipt-form-section-title">Basic Information</div>
            <div className="receipt-form-grid">
              <FormField label="Date" id="receiptDate">
                <ReactDatePicker
                  selected={receiptDate}
                  onChange={date => setReceiptDate(date)}
                  className="form-field-input"
                  dateFormat="dd-MM-yyyy"
                  placeholderText="Select a date"
                  disabled={true}
                  name="receiptDate"
                />
              </FormField>
              
              {settings.userWise && user?.isOwner == true && (
                <>
                  <FormField label="User" id="user" required>
                    <Select
                      options={userList.map(b => ({ value: b.tallyUserId, label: `${b.userName} - ${b.tallyUserId}` }))}
                      value={
                        selectedUserId && selectedUserLabel
                          ? userList.find(u => u.tallyUserId === selectedUserId)
                            ? { value: selectedUserId, label: `${selectedUserLabel} - ${selectedUserId}` }
                            : null
                          : null
                      }
                      onChange={handleUserChange}
                      onBlur={() => {}} // Prevent clearing on blur
                      placeholder="Select User"
                      isClearable
                      isSearchable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </FormField>
                  
                  <FormField label="Receiver" id="receiver">
                    <Select
                      options={receiverOptions.map(b => ({ value: b.value, label: b.label }))}
                      value={selectedReceiver ? { value: selectedReceiver, label: selectedReceiver } : null}
                      onChange={(opt) => {
                        handleReceiverChange({ target: { value: opt ? opt.value : "" } });
                      }}
                      placeholder="Select Receiver"
                      isClearable
                      isSearchable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </FormField>
                </>
              )}
              
              {settings.beatWise && (
                <FormField label="Beat" id="beat" required>
                  <Select
                    options={beats.map(b => ({ value: b.beatName, label: b.beatName }))}
                    value={selectedBeat ? { value: selectedBeat, label: selectedBeat } : null}
                    onChange={(opt) => {
                      handleBeatChange({ target: { value: opt ? opt.value : "" } });
                    }}
                    placeholder="Select Beat"
                    isClearable
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </FormField>
              )}
              
              <FormField label="Customer" id="customer" required>
                <Select
                  options={customers.map(c => ({ value: c.customerName, label: c.customerName }))}
                  value={selectedCustomer ? { value: selectedCustomer, label: selectedCustomer } : null}
                  onChange={(opt) => {
                    handleCustomerChange({ target: { value: opt ? opt.value : "" } });
                  }}
                  placeholder="Select Customer"
                  isClearable
                  isSearchable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </FormField>
              
              <div className="receipt-form-grid-full">
                <div className="receipt-pending-badge">
                  <span className="receipt-pending-badge-label">Pending Amount:</span>
                  <span className="receipt-pending-badge-value">₹ {totalRemainingAmount ?? 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information Section */}
          <div className="receipt-form-section">
            <div className="receipt-form-section-title">Payment Information</div>
            <div className="receipt-form-grid">
              <FormField
                label="Cash Amount"
                id="cashAmount"
                name="cashAmount"
                type="number"
                value={form.cashAmount}
                onChange={handleChange}
                placeholder="Enter cash amount"
                step="any"
                pattern="[0-9]*"
                onKeyUp={handleAdvanceAmount}
                onKeyDown={handleOnlyNumber}
              />
              
              <FormField
                label="UPI Amount"
                id="upiAmount"
                name="upiAmount"
                type="number"
                value={form.upiAmount || ''}
                onChange={handleChange}
                placeholder="Enter UPI amount"
                step="any"
                pattern="[0-9]*"
                onKeyUp={handleAdvanceAmount}
                onKeyDown={handleOnlyNumber}
              />
              
              <FormField
                label="UPI Number"
                id="upiNumber"
                name="upiNumber"
                type="text"
                value={form.upiNumber || ''}
                onChange={handleChange}
                placeholder="Enter UPI number"
                disabled={!(parseFloat(form.upiAmount || 0) > 0)}
              />
              
              <FormField label="UPI Date" id="upiDate">
                <ReactDatePicker
                  selected={upiDate}
                  onChange={date => setUpiDate(date)}
                  dateFormat="dd-MM-yyyy"
                  className="form-field-input"
                  placeholderText="Select UPI date"
                  disabled={!(parseFloat(form.upiAmount || 0) > 0)}
                />
              </FormField>
              
              <FormField
                label="UPI File"
                id="upiFileImage"
                name="upiFileImage"
                type="file"
                onChange={e => setUpiFile(e.target.files[0])}
                accept=".jpg,.jpeg,.png,.pdf"
                disabled={!(parseFloat(form.upiAmount || 0) > 0)}
              />
              
              <FormField
                label="Cheque Amount"
                id="chequeAmount"
                name="chequeAmount"
                type="number"
                value={form.chequeAmount || ''}
                onChange={handleChange}
                placeholder="Enter cheque amount"
                step="any"
                pattern="[0-9]*"
                onKeyDown={handleOnlyNumber}
                onKeyUp={handleAdvanceAmount}
              />
              
              <FormField
                label="Cheque Number"
                id="chequeNumber"
                name="chequeNumber"
                type="text"
                value={form.chequeNumber || ''}
                onChange={handleChange}
                placeholder="Enter cheque number"
                disabled={!(parseFloat(form.chequeAmount || 0) > 0)}
              />
              
              <FormField label="Cheque Date" id="chequeDate">
                <ReactDatePicker
                  selected={chequeDate}
                  onChange={date => setChequeDate(date)}
                  dateFormat="dd-MM-yyyy"
                  className="form-field-input"
                  placeholderText="Select cheque date"
                  disabled={!(parseFloat(form.chequeAmount || 0) > 0)}
                />
              </FormField>
              
              <FormField
                label="Advance Amount"
                id="advanceAmount"
                name="advanceAmount"
                type="number"
                value={form.advanceAmount || ''}
                onChange={handleChange}
                placeholder="Auto-calculated"
                step="any"
                pattern="[0-9]*"
                onKeyDown={handleOnlyNumber}
                readOnly
                helperText="This amount is automatically calculated"
              />
            </div>
          </div>


          {/* Pending Bills Section - Only show if billWise is enabled */}
          {settings.billWise && (
          <div className="receipt-form-section receipt-bills-section">
            <div className="receipt-bills-title-wrapper">
              <div className="receipt-bills-title">Pending Bills</div>
              {vouchers.length > 0 && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => autoAllocateBillsAndAdvance(vouchers, form, setForm)}
                  className="receipt-auto-adjust-button"
                >
                  Auto Adjust Bills
                </Button>
              )}
            </div>
            <div className="receipt-bills-table-wrapper">
              <table className="receipt-bills-table">
                <thead>
                  <tr>
                    <th>Bill Information</th>
                    <th>Allocation Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={2} className="receipt-form-loading">
                        Loading bills...
                      </td>
                    </tr>
                  ) : vouchers.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="receipt-form-empty">
                        No pending bills found.
                      </td>
                    </tr>
                  ) : (
                    vouchers.map((row, idx) => (
                      <tr key={row.billNumber}>
                        <td>
                          <div className="bill-info">
                            <span className="bill-number">{row.billNumber ?? '-'}</span>
                            <span className="bill-detail">
                              Date: {row.billDate ? row.billDate.slice(0, 10) : '-'}
                            </span>
                            <span className="bill-detail">
                              Balance: ₹ {row.balance ?? row.remainingAmount ?? '-'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            className={`bill-amount-input ${form.billErrors?.[row.billNumber] ? 'error' : ''}`}
                            placeholder="Enter amount"
                            value={getAllocationValue(row.billNumber) || ''}
                            min={0}
                            onChange={e => handleBillAllocations(row.billNumber, e.target.value, row)}
                            step="any"
                            onKeyDown={handleOnlyNumber}
                            onKeyUp={handleAdvanceAmount}
                            pattern="[0-9]*"
                          />
                          {form.billErrors?.[row.billNumber] && (
                            <div className="bill-error-message">
                              {form.billErrors[row.billNumber]}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Remarks Section */}
          <div className="receipt-form-section">
            <FormField label="Remark" id="remark" name="remark">
              <textarea
                className="form-field-input"
                placeholder="Enter any additional remarks"
                name="remark"
                value={form.remark || ''}
                onChange={handleChange}
                rows={3}
              />
            </FormField>
          </div>

          {/* Form Actions */}
          {vouchers.length > 0 && (
            <div className="receipt-form-actions">
              <Button
                type="submit"
                variant="success"
                disabled={saving}
                loading={saving}
                className="receipt-form-submit-button"
              >
                Save Receipt
              </Button>
            </div>
          )}
          
          {msg && (
            <div className={`receipt-form-message ${msg.includes('successfully') ? 'success' : 'error'}`}>
              {msg}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

